import type { PaginationState, SortingState } from '@tanstack/react-table'
import {
  legacyCreateColumnHelper as createColumnHelper,
  type LegacyColumnDef,
} from '@tanstack/react-table/legacy'
import { useMemo } from 'react'

import { DataTable } from '@/components/data-table/DataTable'
import { parseColumnFilters, serializeColumnFilters } from '@/components/filters/column-filter'
import { FilterBar, FilterField } from '@/components/filters/FilterBar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  COLUMN_FILTER_KINDS,
  applyColumnFilters,
  datasetId,
  distinctValues,
} from '@/features/report/reconcile/column-filters'
import {
  issueCodesText,
  orUnknown,
  signedKg,
  tartiNoText,
  ton,
} from '@/features/report/reconcile/format'
import { RECORD_PARAMS } from '@/features/report/reconcile/params'
import {
  CardList,
  CodeBadges,
  RecordCard,
  RecordStatusCell,
} from '@/features/report/reconcile/parts'
import type { ReportFilters } from '@/features/report/use-report-filters'
import { useIsMobile } from '@/hooks/use-mobile'
import { useTablePreferences } from '@/hooks/use-table-preferences'
import { EMPTY_VALUE, formatCubeDateTime, formatDateOnly, formatInteger } from '@/lib/format'
import {
  RECORD_STATUSES,
  diffKg,
  filterRecords,
  isRecordSortKey,
  issueCodes,
  sortRecords,
  type ReconcileRecord,
  type RecordStatus,
} from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

const t = messages.reconcile
const col = t.columns

const TABLE_ID = 'rpr183-reconcile'
const NO_HIDDEN_COLUMNS = {}

const helper = createColumnHelper<ReconcileRecord>()

const numberCell = (text: string, className?: string | false) => (
  <span className={cn('tabular block text-right', className)}>{text}</span>
)

// Sorunu anlatan kolonlar (Fark'a kadar) önde: 1440 px'te yatay kaydırmadan okunsun;
// plaka, tarih ve cari sonda (detay panelinde de var).
// Genişlikler başlıktaki tutamaç, sıralama oku ve huniyle birlikte etiket kesilmesin diye.
// `sortDescFirst: false`: TanStack sayı kolonlarında ilk tıkta azalan sıralıyor.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- bkz. DataTableProps.columns
const BASE_COLUMNS: LegacyColumnDef<ReconcileRecord, any>[] = [
  helper.accessor((record) => t.status[record.status], {
    id: 'status',
    header: col.status,
    size: 140,
    sortDescFirst: false,
    cell: ({ row }) => <RecordStatusCell record={row.original} />,
  }),
  helper.accessor((record) => issueCodes(record).join(', '), {
    id: 'codes',
    header: col.codes,
    size: 190,
    enableSorting: false,
    // Kodlar adlarıyla geniş: birden fazlası alt alta sarılır.
    cell: ({ row }) => <CodeBadges record={row.original} />,
    meta: { exportValue: issueCodesText },
  }),
  helper.accessor((record) => record.kantarAdi, {
    id: 'kantar',
    header: col.kantar,
    size: 130,
    sortDescFirst: false,
    cell: ({ getValue }) => getValue(),
  }),
  helper.accessor((record) => record.tartino, {
    id: 'tartino',
    header: col.tartino,
    size: 135,
    sortDescFirst: false,
    cell: ({ getValue }) => numberCell(tartiNoText(getValue())),
  }),
  helper.accessor((record) => record.kantarTon, {
    id: 'kantarTon',
    header: col.kantarTon,
    size: 165,
    sortDescFirst: false,
    cell: ({ getValue }) => numberCell(ton(getValue())),
    meta: { exportNumberFormat: '#,##0.000' },
  }),
  helper.accessor((record) => record.diaTon, {
    id: 'diaTon',
    header: col.diaTon,
    size: 165,
    sortDescFirst: false,
    cell: ({ getValue }) => numberCell(ton(getValue())),
    meta: { exportNumberFormat: '#,##0.000' },
  }),
  helper.accessor(diffKg, {
    id: 'diff',
    header: col.diffKg,
    size: 140,
    sortDescFirst: false,
    cell: ({ getValue }) => {
      const kg = getValue()
      if (kg === null) return numberCell(EMPTY_VALUE)
      return numberCell(signedKg(kg), kg !== 0 && 'text-destructive font-medium')
    },
    meta: { exportNumberFormat: '#,##0' },
  }),
  helper.accessor((record) => orUnknown(record.plaka), {
    id: 'plaka',
    header: col.plaka,
    size: 125,
    sortDescFirst: false,
    cell: ({ getValue }) => getValue(),
  }),
  helper.accessor((record) => formatDateOnly(record.tarih), {
    id: 'tarih',
    header: col.tarih,
    size: 125,
    sortDescFirst: false,
    cell: ({ getValue }) => getValue(),
  }),
  helper.accessor((record) => formatCubeDateTime(record.giris), {
    id: 'giris',
    header: col.giris,
    size: 165,
    sortDescFirst: false,
    cell: ({ getValue }) => <span className="tabular">{getValue()}</span>,
  }),
  helper.accessor((record) => formatCubeDateTime(record.kantar?.cikis), {
    id: 'cikis',
    header: col.cikis,
    size: 165,
    sortDescFirst: false,
    cell: ({ getValue }) => <span className="tabular">{getValue()}</span>,
  }),
  helper.accessor((record) => orUnknown(record.cari), {
    id: 'cari',
    header: col.cari,
    size: 240,
    sortDescFirst: false,
    cell: ({ getValue }) => <span title={getValue()}>{getValue()}</span>,
  }),
  helper.accessor((record) => record.override?.note ?? '', {
    id: 'note',
    header: col.note,
    size: 240,
    sortDescFirst: false,
    cell: ({ getValue }) => (
      <span className="line-clamp-2 whitespace-normal" title={getValue()}>
        {getValue()}
      </span>
    ),
  }),
]

/** Başlık hunisi: `filterKind` taşıyan kolonlarda çıkar (bkz. `DataTable`). */
const COLUMNS = BASE_COLUMNS.map((column) => {
  const filterKind = column.id ? COLUMN_FILTER_KINDS.get(column.id) : undefined
  return filterKind ? { ...column, meta: { ...column.meta, filterKind } } : column
})

const isStatus = (value: string): value is RecordStatus =>
  (RECORD_STATUSES as string[]).includes(value)

function parseSorting(raw: string | undefined): SortingState {
  return (raw ?? '')
    .split(',')
    .map((item) => ({ id: item.replace(/^-/, ''), desc: item.startsWith('-') }))
    .filter((item) => isRecordSortKey(item.id))
}

const withUnit = (value: string, unit: string) =>
  value === EMPTY_VALUE ? value : `${value} ${unit}`

function MobileRecordCard({
  record,
  onOpen,
}: {
  record: ReconcileRecord
  onOpen: (record: ReconcileRecord) => void
}) {
  const diff = diffKg(record)
  return (
    <RecordCard record={record} onOpen={onOpen}>
      <span className="tabular text-xs">
        {t.mobile.tonnage(
          withUnit(ton(record.kantarTon), 't'),
          withUnit(ton(record.diaTon), 't'),
          diff === null ? EMPTY_VALUE : `${signedKg(diff)} kg`,
        )}
      </span>
      <span className="text-muted-foreground tabular text-xs">
        {t.mobile.times(formatCubeDateTime(record.giris), formatCubeDateTime(record.kantar?.cikis))}
      </span>
    </RecordCard>
  )
}

/**
 * Tüm kayıtlar: süzme, sıralama ve sayfalama istemcide (veri zaten tarayıcıda eşleşiyor).
 * Durum süzmesi yalnız üstteki durum kartlarından gelir; burada yalnız arama ve (masaüstünde)
 * başlık hunileri var. Telefonda tablo yerine kart listesi.
 */
export function RecordsTab({
  records,
  filters,
  onOpen,
}: {
  records: ReconcileRecord[]
  filters: ReportFilters
  onOpen: (record: ReconcileRecord) => void
}) {
  const isMobile = useIsMobile()
  const { get, getList, set } = filters
  const statuses = getList(RECORD_PARAMS.status).filter(isStatus)
  const search = get(RECORD_PARAMS.search) ?? ''
  const sortRaw = get('sort')
  const pageSize = Number(get('page_size') ?? '50')

  const columnFiltersRaw = get(RECORD_PARAMS.columns)
  const columnFilters = useMemo(
    () => parseColumnFilters(columnFiltersRaw, COLUMN_FILTER_KINDS),
    [columnFiltersRaw],
  )

  const statusKey = statuses.join(',')
  const scoped = useMemo(
    () => filterRecords(records, { statuses, kantars: [], codes: [], search }),
    // `statusKey`: liste parametresi her çizimde yeni dizi döner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, statusKey, search],
  )
  // Huniler yalnız masaüstü tablosunda var; telefonda görünmeyen bir filtre uygulanmasın.
  const visible = useMemo(
    () =>
      sortRecords(
        isMobile ? scoped : applyColumnFilters(scoped, columnFilters),
        parseSorting(sortRaw),
      ),
    [scoped, columnFilters, sortRaw, isMobile],
  )

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize))
  const pageIndex = Math.min(Math.max(Number(get('page') ?? '1') - 1, 0), pageCount - 1)
  const pagination: PaginationState = { pageIndex, pageSize }
  const sorting = parseSorting(sortRaw)
  const preferences = useTablePreferences(TABLE_ID, NO_HIDDEN_COLUMNS)

  const change = (patch: Record<string, string | string[] | undefined>) =>
    set({ ...patch, page: undefined })
  const shown = t.filter.shown(formatInteger(visible.length), formatInteger(records.length))

  return (
    <div className="flex flex-col gap-3">
      <FilterBar
        onReset={() =>
          change(Object.fromEntries(Object.values(RECORD_PARAMS).map((key) => [key, undefined])))
        }
      >
        <FilterField label={t.filter.search} htmlFor="reconcile-search">
          <Input
            id="reconcile-search"
            type="search"
            className="w-full sm:w-64"
            placeholder={t.filter.searchPlaceholder}
            value={search}
            onChange={(event) => change({ [RECORD_PARAMS.search]: event.target.value })}
          />
        </FilterField>
      </FilterBar>

      {statuses.length > 0 && (
        <p className="flex flex-wrap items-center gap-x-2 text-sm">
          {t.filter.statusActive(statuses.map((status) => t.status[status]).join(', '))}
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => change({ [RECORD_PARAMS.status]: undefined })}
          >
            {t.filter.showAll}
          </Button>
        </p>
      )}

      {isMobile ? (
        <>
          <p className="text-sm font-medium">{shown}</p>
          {visible.length === 0 ? (
            <p className="text-muted-foreground p-4 text-center text-sm">{t.filter.empty}</p>
          ) : (
            <CardList
              // Süzme değişince liste baştan açılsın.
              key={`${statusKey}|${search}|${sortRaw ?? ''}`}
              items={visible}
              render={(record) => (
                <MobileRecordCard key={record.key} record={record} onOpen={onOpen} />
              )}
            />
          )}
        </>
      ) : (
        <DataTable
          tableId={TABLE_ID}
          title={shown}
          columns={COLUMNS}
          data={visible.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)}
          exportData={visible}
          exportFilename={t.exportFilename}
          total={visible.length}
          pagination={pagination}
          onPaginationChange={(updater) => {
            const next = typeof updater === 'function' ? updater(pagination) : updater
            set({ page: String(next.pageIndex + 1), page_size: String(next.pageSize) })
          }}
          sorting={sorting}
          onSortingChange={(updater) => {
            const next = typeof updater === 'function' ? updater(sorting) : updater
            const serialised = next.map((item) => `${item.desc ? '-' : ''}${item.id}`).join(',')
            change({ sort: serialised || undefined })
          }}
          emptyMessage={t.filter.empty}
          layout={preferences.layout}
          onLayoutChange={preferences.save}
          onLayoutReset={preferences.reset}
          columnFilters={columnFilters}
          onColumnFiltersChange={(next) =>
            change({ [RECORD_PARAMS.columns]: serializeColumnFilters(next) })
          }
          filterValues={(columnId, text) => ({
            queryKey: [
              'reconcile-column-values',
              datasetId(scoped),
              columnFiltersRaw,
              columnId,
              text,
            ],
            queryFn: () =>
              distinctValues(applyColumnFilters(scoped, columnFilters, columnId), columnId, text),
            staleTime: Infinity,
          })}
          onRowClick={onOpen}
        />
      )}
    </div>
  )
}
